<script setup lang="ts">
import { ref, watch, onMounted, onBeforeUnmount } from "vue";
import SvgIcon from "@SharedVueComponents/SvgIcon/SvgIcon.vue";
import CloseButton from "@SharedVueComponents/CloseButton/CloseButton.vue";
import OcmMessenger from "ocm_mercurius_messenger";
import { messengerPublicationTypes } from "@ocm/services/services.constants";
import { GlobalRouterMessengerData } from "@/shared/types/messenger.type";
import { shopContextPath } from "@ocm/services/ocm-object";
import { formatPrice } from "@ocm/services/services.numberFormatter";
import pushDataLayer from "@ocm/services/dataLayer";

type EmarsysConfig = {
	cartMinValue?: number;
};
const CART_MIN_VALUE_DEFAULT = 100;
const TOAST_DELAY = 5 * 1000;

const cartUrl = ref(`${shopContextPath}/cart/`);
const _isCart = () => window.location.pathname.includes("/cart/");
const _isPDP = () => {
	const lastSlash = window.location.pathname.lastIndexOf("/");
	const maybeProductCode = window.location.pathname.substring(lastSlash - 9, lastSlash);
	return maybeProductCode.length && !Number.isNaN(+maybeProductCode);
};

// Tracking
const trackGA3Toast = (action: string) => {
	const pagePath = window.recommendationData?.Recommendation?.pageData?.pagePath ?? "";
	pushDataLayer({
		event: "GAevent",
		event_category: "S_jetztSparenLayer",
		event_action: action,
		event_label: pagePath,
	});
};

const routeToCart = () => {
	if (_isCart()) return;
	trackGA3Toast("S_cart");
	window.ocmOrchestrator ? window.ocmOrchestrator.spaNavigateUrl(cartUrl.value) : window.location.assign(cartUrl.value);
};

const hasToast = ref(false);
const removeToast = () => {
	hasToast.value = false;
};
const isToastVisible = ref(true);
const _setIsToastVisible = (val: boolean) => {
	isToastVisible.value = val;
};
const savingTotal = ref("");
watch(savingTotal, (val) => {
	if (!val) removeToast();
});

let _delayTimeout: number;
const _setupToast = (cartMinValue = CART_MIN_VALUE_DEFAULT) => {
	if (sessionStorage.getItem("cartToastShown")) return;

	clearTimeout(_delayTimeout);
	_delayTimeout = setTimeout(() => {
		const cartData = window.recommendationData?.Recommendation?.customerData?.cart;
		// abbrechen, wenn der Warenkorb leer ist oder sein Gesamtwert einen definierten Mindestwert unterschreitet
		if (!cartData?.products?.length || +cartData.cartTotal < cartMinValue) return;

		if (!savingTotal.value) {
			const saving = cartData.products.reduce(
				(acc, { msrp, price, quantity }) => acc + (+msrp - +price) * +quantity,
				0
			);
			savingTotal.value = formatPrice(saving, cartData.currency);
		}

		if (_isCart() || _isPDP()) _setIsToastVisible(false);
		hasToast.value = true;
		trackGA3Toast("S_show");
		try {
			sessionStorage.setItem("cartToastShown", "1");
		} catch (error) {
			console.error(error);
		}
	}, TOAST_DELAY);
};

onMounted(() => {
	OcmMessenger.subscribe(messengerPublicationTypes.UPDATED_CART_SAVING, ({ data }: { data: string }) => {
		savingTotal.value = data;
	});
	OcmMessenger.subscribe(messengerPublicationTypes.ADD_EMARSYS_TOAST, ({ data }: { data?: EmarsysConfig }) => {
		_setupToast(data?.cartMinValue);
	});
	OcmMessenger.subscribe(
		messengerPublicationTypes.ROUTE_CHANGE_MOUNTED_APPS,
		({ data: { mountedApps } }: GlobalRouterMessengerData) => {
			_setIsToastVisible(!mountedApps.includes("mf-cart") && !_isPDP());
		}
	);
	OcmMessenger.subscribe(messengerPublicationTypes.ENTER_PRODUCTDETAIL, () => {
		_setIsToastVisible(false);
	});
	OcmMessenger.subscribe(messengerPublicationTypes.LEAVE_PRODUCTDETAIL, () => {
		_setIsToastVisible(true);
	});
});

onBeforeUnmount(() => {
	clearTimeout(_delayTimeout);
	removeToast();
});
</script>

<template>
	<Teleport to="#emarsys-toast-container">
		<transition name="emarsys-toast">
			<!-- eslint-disable-next-line vuejs-accessibility/click-events-have-key-events eslint-disable-next-line vuejs-accessibility/no-static-element-interactions -->
			<div
				v-if="hasToast && isToastVisible"
				class="emarsys-toast ocm-body1 pointer-events-auto relative mb-2 grid min-h-5 max-w-96 grid-cols-[4.8571428571rem,1fr] justify-self-center rounded-lg bg-gray-beton focus:outline focus:outline-2 focus:outline-offset-2 focus:outline-current"
				tabindex="-1"
				@click="
					removeToast();
					routeToCart();
				"
			>
				<CloseButton
					class="close-button right-0 top-1 border-transparent"
					inverted
					aria-label="Nachricht schließen"
					@click.stop="
						removeToast();
						trackGA3Toast('S_closeX');
					"
				/>

				<div class="diagonal-lemon grid items-center rounded-l-lg px-2.5">
					<SvgIcon class="h-7 w-7" icon-name="circle-percent" />
				</div>
				<div class="py-4 pr-10">
					<div class="ocm-headline3 mb-1">Sie sparen {{ savingTotal }}</div>
					<div>
						Auf die Artikel
						<a
							:href="cartUrl"
							class="inline-block underline after:inline-block after:whitespace-pre after:content-['_'] last:after:content-none hover:text-gray-90"
							@click.prevent
						>
							in Ihrem Warenkorb
						</a>
					</div>
				</div>
			</div>
		</transition>
	</Teleport>
</template>

<style lang="postcss">
.emarsys-toast {
	--emarsys-toast-enter-duration: 220ms;
	--emarsys-toast-enter-opacity: 1;
	--emarsys-toast-enter-translate: 0 100%;
	box-shadow: 0 5px 5px rgba(0, 0, 0, 0.2), 0 3px 14px rgba(0, 0, 0, 0.12), 0 8px 10px rgba(0, 0, 0, 0.14);

	@media (min-width: 64em) {
		--emarsys-toast-enter-duration: 320ms;
		--emarsys-toast-enter-opacity: 0.33;
		--emarsys-toast-enter-translate: 100%;
	}

	@media (prefers-reduced-motion) {
		--emarsys-toast-enter-duration: 440ms;
		--emarsys-toast-enter-opacity: 0;
		--emarsys-toast-enter-translate: 0;
	}
}

@keyframes emarsys-toast-slide-in {
	from {
		opacity: var(--emarsys-toast-enter-opacity);
		translate: var(--emarsys-toast-enter-translate);
	}
	to {
		opacity: unset;
		translate: unset;
	}
}

.emarsys-toast-enter-active {
	animation: emarsys-toast-slide-in var(--emarsys-toast-enter-duration) ease-out forwards;
}

.emarsys-toast-leave-active {
	/* `toast-fade-in` is defined in '_toasts.scss' */
	animation: toast-fade-in 360ms ease-in reverse forwards;
}

.diagonal-lemon {
	background-image: conic-gradient(at 100% 0%, transparent 202deg, rgb(221, 220, 2) 0deg);
}
</style>

<style scoped>
.close-button {
	width: 2.8571429rem !important;
	height: 2.8571429rem !important;
	background-color: transparent !important;
}
</style>
